import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, Copy, X } from "lucide-react";
import { toast } from "sonner";
import { initials, label } from "../api";

export const TONES = {
  slate: "bg-zinc-100 text-zinc-700 border-zinc-200 dark:bg-zinc-500/10 dark:text-zinc-300 dark:border-zinc-500/30", zinc: "bg-zinc-100 text-zinc-700 border-zinc-200 dark:bg-zinc-500/10 dark:text-zinc-300 dark:border-zinc-500/30",
  sky: "bg-sky-50 text-sky-700 border-sky-200 dark:bg-sky-500/10 dark:text-sky-300 dark:border-sky-500/30", blue: "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-500/10 dark:text-blue-300 dark:border-blue-500/30",
  indigo: "bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-500/10 dark:text-indigo-300 dark:border-indigo-500/30", purple: "bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-500/10 dark:text-violet-300 dark:border-violet-500/30",
  amber: "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:border-amber-500/30", orange: "bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-500/10 dark:text-orange-300 dark:border-orange-500/30",
  emerald: "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:border-emerald-500/30", teal: "bg-teal-50 text-teal-700 border-teal-200 dark:bg-teal-500/10 dark:text-teal-300 dark:border-teal-500/30",
  rose: "bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-500/10 dark:text-rose-300 dark:border-rose-500/30", pink: "bg-pink-50 text-pink-700 border-pink-200 dark:bg-pink-500/10 dark:text-pink-300 dark:border-pink-500/30",
  red: "bg-red-50 text-red-700 border-red-200 dark:bg-red-500/10 dark:text-red-300 dark:border-red-500/30", dark: "bg-zinc-950 text-white border-zinc-950 dark:bg-zinc-100 dark:text-zinc-900 dark:border-zinc-100",
};

export const STATUS_TONES = {
  new_lead: "sky", contacted: "indigo", quote_booked: "purple", quote_completed: "purple", quote_sent: "amber", awaiting_decision: "orange", won: "emerald", lost: "rose",
  scheduled: "blue", in_progress: "amber", waiting_customer: "orange", waiting_materials: "orange", waiting_sub: "orange", on_hold: "slate", completed: "emerald", invoiced: "teal", closed: "slate",
  draft: "slate", sent: "blue", viewed: "indigo", accepted: "emerald", declined: "rose", expired: "slate", rejected: "rose", approved: "emerald", cancelled: "slate", pending: "amber",
  paid: "emerald", part_paid: "amber", overdue: "rose", void: "slate", invited: "blue", complete: "emerald", date_requested: "amber",
  todo: "slate", blocked: "rose", high: "rose", urgent: "red", normal: "slate", low: "sky", admin: "dark", manager: "purple", team_member: "sky",
  planning: "sky", on_track: "emerald", at_risk: "amber", delayed: "rose", done: "emerald", active: "amber", skipped: "slate", open: "amber", resolved: "emerald", due: "orange",
  on_site: "emerald", working: "amber", travelling: "blue", late: "rose", available: "slate", paused: "orange",
};

export const BAR_COLORS = { sky: "#38bdf8", blue: "#3b82f6", indigo: "#6366f1", purple: "#8b5cf6", amber: "#f59e0b", orange: "#f97316", emerald: "#10b981", teal: "#14b8a6", rose: "#f43f5e", red: "#ef4444", slate: "#a1a1aa", zinc: "#a1a1aa", pink: "#ec4899" };
export const HEALTH_LABEL = { on_track: "On track", at_risk: "At risk", delayed: "Delayed", completed: "Completed" };

export function Badge({ tone, status, children, testid, className = "", dot = true }) {
  const t = tone || STATUS_TONES[status] || "slate";
  return <span data-testid={testid} className={`chip ${TONES[t] || TONES.slate} ${className}`}>{dot && <span className="dot" />}{children ?? label(status)}</span>;
}

export function Pills({ options, value, onChange, testid = "pills", size }) {
  return (
    <div className="pill-group" data-testid={testid}>
      {options.map((o) => {
        const v = typeof o === "string" ? o : o.value;
        const l = typeof o === "string" ? label(o) : o.label;
        return <button key={v} type="button" data-testid={`${testid}-${v}`} onClick={() => onChange(v)} className={`pill capitalize ${value === v ? "pill-active" : ""} ${size === "sm" ? "!px-2.5 !py-1 !text-[11px]" : ""}`}>{l}</button>;
      })}
    </div>
  );
}

export function PageHeader({ eyebrow, title, subtitle, actions, testid, children }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-3" data-testid={testid}>
      <div className="min-w-0">
        {eyebrow && <div className="eyebrow mb-0.5">{eyebrow}</div>}
        <h1 className="h1">{title}</h1>
        {subtitle && <p className="t3 mt-0.5 max-w-2xl text-[13px]">{subtitle}</p>}
        {children}
      </div>
      {actions && <div className="toolbar">{actions}</div>}
    </div>
  );
}

export function Stat({ label: l, value, sub, icon: Icon, tone = "slate", to, testid, warn, progress }) {
  const body = (
    <>
      <div className="row"><span className="eyebrow">{l}</span>{Icon && <Icon size={14} className="t4" />}</div>
      <div className={`stat-value ${warn ? "!text-rose-600 dark:!text-rose-400" : ""}`}>{value}</div>
      {progress != null && <div className="mt-2"><Progress value={progress} tone={tone === "slate" ? "amber" : tone} /></div>}
      {sub && <div className="t3 mt-1 text-xs">{sub}</div>}
    </>
  );
  const cls = "card card-hover block p-3.5";
  return to ? <Link to={to} className={cls} data-testid={testid}>{body}</Link> : <div className={cls} data-testid={testid}>{body}</div>;
}

export function Kpi({ label: l, value, sub, tone, testid, to, icon: Icon }) {
  const c = { rose: "text-rose-600 dark:text-rose-400", emerald: "text-emerald-600 dark:text-emerald-400", amber: "text-amber-600 dark:text-amber-400", blue: "text-blue-600 dark:text-blue-400" }[tone] || "t1";
  const body = <><div className="flex items-center gap-1.5 eyebrow">{Icon && <Icon size={12} />}{l}</div><div className={`money mt-1 font-display text-lg font-bold tracking-tight sm:text-xl ${c}`}>{value}</div>{sub && <div className="t3 mt-0.5 truncate text-[11px]">{sub}</div>}</>;
  return to ? <Link to={to} className="strip-cell block min-w-0 transition-colors hover:bg-surface-2" data-testid={testid}>{body}</Link> : <div className="strip-cell min-w-0" data-testid={testid}>{body}</div>;
}

export function Avatar({ name, size = "md", tone = "slate", className = "" }) {
  const s = { xs: "h-5 w-5 text-[8px]", sm: "h-6 w-6 text-[9px]", md: "h-8 w-8 text-[11px]", lg: "h-11 w-11 text-sm", xl: "h-14 w-14 text-base" }[size];
  const palette = ["bg-sky-100 text-sky-800 dark:bg-sky-500/20 dark:text-sky-200", "bg-emerald-100 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-200", "bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-200", "bg-violet-100 text-violet-800 dark:bg-violet-500/20 dark:text-violet-200", "bg-pink-100 text-pink-800 dark:bg-pink-500/20 dark:text-pink-200", "bg-teal-100 text-teal-800 dark:bg-teal-500/20 dark:text-teal-200"];
  const idx = (name || "").split("").reduce((a, c) => a + c.charCodeAt(0), 0) % palette.length;
  return <span className={`inline-flex shrink-0 items-center justify-center rounded-full font-bold ${s} ${tone === "slate" ? palette[idx] : TONES[tone]} ${className}`}>{initials(name)}</span>;
}

function useEsc(open, onClose) {
  useEffect(() => {
    if (!open) return;
    const h = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open, onClose]);
}

export function Modal({ open, onClose, title, subtitle, children, testid, wide }) {
  useEsc(open, onClose);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center bg-zinc-950/60 p-0 backdrop-blur-[2px] sm:items-center sm:p-6" onClick={onClose}>
      <div data-testid={testid} onClick={(e) => e.stopPropagation()} className={`card animate-in max-h-[92vh] w-full overflow-y-auto rounded-b-none p-5 shadow-[0_24px_60px_-20px_rgba(0,0,0,0.45)] sm:rounded-lg ${wide ? "max-w-3xl" : "max-w-lg"}`} style={{ borderColor: "var(--line-strong)" }}>
        <div className="mb-4 flex items-start justify-between gap-3">
          <div><h3 className="h2">{title}</h3>{subtitle && <p className="t3 mt-0.5 text-[13px]">{subtitle}</p>}</div>
          <button data-testid={`${testid}-close`} onClick={onClose} className="btn-icon"><X size={16} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function Drawer({ open, onClose, title, subtitle, children, testid, header, wide }) {
  useEsc(open, onClose);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[55] flex justify-end bg-zinc-950/50 backdrop-blur-[2px]" onClick={onClose}>
      <div data-testid={testid} onClick={(e) => e.stopPropagation()} className={`flex h-full w-full flex-col border-l border-line bg-surface shadow-2xl animate-in ${wide === "xl" ? "max-w-[1320px]" : wide ? "max-w-3xl" : "max-w-xl"}`}>
        <div className="flex items-start justify-between gap-3 border-b border-line px-5 py-3.5">
          <div className="min-w-0 flex-1">{header || <><h3 className="h2 truncate">{title}</h3>{subtitle && <p className="t3 mt-0.5 text-[13px]">{subtitle}</p>}</>}</div>
          <button data-testid={`${testid}-close`} onClick={onClose} className="btn-icon"><X size={16} /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
      </div>
    </div>
  );
}

export function CopyButton({ text, label: l = "Copy link", testid, className = "btn-secondary btn-sm", absolute = true }) {
  return (
    <button data-testid={testid} type="button" className={className} onClick={() => { navigator.clipboard.writeText(absolute && text?.startsWith("/") ? window.location.origin + text : text); toast.success("Link copied"); }}>
      <Copy size={13} /> {l}
    </button>
  );
}

export function Empty({ children, action, testid, icon: Icon }) {
  return <div className="empty" data-testid={testid}>{Icon && <Icon size={18} className="t4 mx-auto mb-2" />}{children}{action && <div className="mt-3">{action}</div>}</div>;
}

export function Loading({ label: l = "Loading…", rows = 4 }) {
  return (
    <div className="space-y-3 py-2" data-testid="loading" aria-label={l}>
      <div className="skeleton h-7 w-56" />
      <div className="skeleton h-4 w-80" />
      <div className="grid gap-3 sm:grid-cols-3">{Array.from({ length: rows }).map((_, i) => <div key={i} className="skeleton h-24" />)}</div>
      <div className="skeleton h-40" />
    </div>
  );
}

export function Skeleton({ className = "" }) { return <div className={`skeleton ${className}`} />; }

// Shared failure state — used when a record can't be loaded (404 / no access / network) so screens never sit on a skeleton forever.
export function ErrorState({ error, title, back, backLabel = "Go back", onRetry, testid = "error-state" }) {
  const status = error?.response?.status;
  const heading = title || (status === 404 ? "We couldn't find that" : status === 403 ? "You don't have access to this" : "Something went wrong");
  const detail = status === 404 ? "It may have been deleted, or the link is out of date." : status === 403 ? "Ask an admin in your business if you need access." : (typeof error?.response?.data?.detail === "string" ? error.response.data.detail : "Please try again in a moment.");
  return (
    <div className="card mx-auto max-w-md p-8 text-center animate-in" data-testid={testid}>
      <div className="ref t4 text-[11px]">{status ? `Error ${status}` : "Error"}</div>
      <h2 className="h1 mt-2 text-[20px]">{heading}</h2>
      <p className="t3 mt-2 text-sm">{detail}</p>
      <div className="mt-5 flex justify-center gap-2">
        {back && <Link to={back} className="btn-secondary btn-sm" data-testid={`${testid}-back`}>{backLabel}</Link>}
        {onRetry && <button className="btn-primary btn-sm" onClick={onRetry} data-testid={`${testid}-retry`}>Try again</button>}
      </div>
    </div>
  );
}

export function Field({ label: l, children, hint, className = "" }) {
  return <div className={className}>{l && <label className="label">{l}</label>}{children}{hint && <p className="t4 mt-1 text-[11px]">{hint}</p>}</div>;
}

export function Progress({ value, tone = "amber", testid, className = "" }) {
  const v = Math.max(0, Math.min(100, value || 0));
  return <div className={`progress ${className}`} data-testid={testid}><div style={{ width: `${v}%`, backgroundColor: BAR_COLORS[tone] || BAR_COLORS.amber }} /></div>;
}

export function SegBar({ segments, total, testid, height = "h-2" }) {
  const sum = total || segments.reduce((s, x) => s + (x.value || 0), 0) || 1;
  return (
    <div className={`flex w-full overflow-hidden rounded-full ${height}`} style={{ background: "var(--line)" }} data-testid={testid}>
      {segments.map((s, i) => <div key={i} title={`${s.label}: ${s.value}`} style={{ width: `${Math.max(0, (s.value / sum) * 100)}%`, background: BAR_COLORS[s.tone] || s.color || BAR_COLORS.amber }} />)}
    </div>
  );
}

export function Section({ title, action, children, className = "", testid, flush, eyebrow, collapsible = false, defaultOpen = true, summary, foldAll = false }) {
  const [open, setOpen] = useState(defaultOpen);
  useEffect(() => { setOpen(defaultOpen); }, [defaultOpen]);
  const folded = collapsible && !open;
  const lg = foldAll ? "" : "lg:";
  const mob = foldAll ? "" : "lg:hidden";
  return (
    <section className={`card min-w-0 ${flush ? "" : "p-4"} ${className}`} data-testid={testid} data-open={collapsible ? String(open) : undefined}>
      {(title || action || collapsible) && (
        <div className={`row ${flush ? "border-b border-line px-4 py-2.5" : folded ? `${lg}mb-3` : "mb-3"} ${folded && flush ? `!border-b-0 ${lg}!border-b` : ""} ${collapsible && foldAll ? "cursor-pointer select-none" : ""}`} onClick={collapsible && foldAll ? () => setOpen((o) => !o) : undefined}>
          <div className="min-w-0 flex-1">{eyebrow && <div className="eyebrow">{eyebrow}</div>}<h3 className="h2 text-[15px]">{title}</h3>{folded && summary && summary !== eyebrow && <div className={`t3 mt-0.5 truncate text-[12.5px] ${mob}`} data-testid={testid ? `${testid}-summary` : undefined}>{summary}</div>}</div>
          {action && <span className="flex shrink-0 items-center gap-2" onClick={(e) => e.stopPropagation()}>{action}</span>}
          {collapsible && <button type="button" className={`btn-icon shrink-0 ${mob}`} onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }} aria-expanded={open} aria-label={open ? "Collapse" : "Expand"} data-testid={testid ? `${testid}-toggle` : undefined}><ChevronDown size={16} className={`transition-transform ${open ? "rotate-180" : ""}`} /></button>}
        </div>
      )}
      {collapsible ? <div className={folded ? (foldAll ? "hidden" : "hidden lg:block") : ""}>{children}</div> : children}
    </section>
  );
}

export function Menu({ trigger, children, testid, align = "right", className = "btn-secondary btn-sm", chevron = true }) {
  const [open, setOpen] = useState(false);
  const ref = useRef();
  useEffect(() => {
    const h = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  return (
    <div className="relative" ref={ref}>
      <button type="button" data-testid={testid} onClick={() => setOpen(!open)} className={className}>{trigger}{chevron && <ChevronDown size={13} />}</button>
      {open && <div onClick={() => setOpen(false)} className={`card animate-in absolute z-30 mt-1 min-w-[200px] overflow-hidden p-1 shadow-[0_10px_38px_-10px_rgba(22,23,24,0.35)] ${align === "right" ? "right-0" : "left-0"}`}>{children}</div>}
    </div>
  );
}

export const MenuItem = ({ onClick, children, testid, danger, icon: Icon, hint }) => (
  <button type="button" data-testid={testid} onClick={onClick} className={`flex w-full items-center gap-2.5 rounded-[5px] px-2.5 py-1.5 text-left text-[13px] transition-colors ${danger ? "text-rose-600 hover:bg-rose-50 dark:text-rose-400 dark:hover:bg-rose-500/10" : "t2 hover:bg-surface-2"}`}>
    {Icon && <Icon size={14} className="t3" />}<span className="flex-1">{children}</span>{hint && <span className="kbd">{hint}</span>}
  </button>
);

export function KV({ k, v, mono, testid }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-line py-1.5 text-[13px] last:border-b-0">
      <span className="t3">{k}</span>
      <span className={`t1 text-right font-medium ${mono ? "money" : ""}`} data-testid={testid}>{v ?? "—"}</span>
    </div>
  );
}

export function Tabs({ tabs, value, onChange, testid = "tab" }) {
  return (
    <div className="tabs">
      {tabs.map((t) => {
        const v = typeof t === "string" ? t : t.value;
        const l = typeof t === "string" ? label(t) : t.label;
        const count = typeof t === "object" ? t.count : undefined;
        return (
          <button key={v} data-testid={`${testid}-${v}`} onClick={() => onChange(v)} className={`tab capitalize ${value === v ? "tab-active" : ""}`}>
            {l}{count != null && count !== 0 && <span className={`ml-1.5 rounded-full px-1.5 py-px tnum text-[10px] font-semibold ${value === v ? "bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-300" : "bg-surface-2 t3"}`}>{count}</span>}
          </button>
        );
      })}
    </div>
  );
}

export function Strip({ items, testid, cols }) {
  return (
    <div className={`strip ${cols || "grid-cols-2 sm:grid-cols-3 lg:grid-cols-6"}`} data-testid={testid}>
      {items.map(([l, v, c, sub]) => <div key={l} className="strip-cell"><div className="eyebrow">{l}</div><div className={`val money ${c || ""}`}>{v}</div>{sub && <div className="t3 text-[11px]">{sub}</div>}</div>)}
    </div>
  );
}

export function Health({ value, testid }) {
  return <Badge status={value} testid={testid}>{HEALTH_LABEL[value] || label(value)}</Badge>;
}

export function Ring({ value = 0, size = 56, stroke = 5, tone = "amber", children }) {
  const r = (size - stroke) / 2, c = 2 * Math.PI * r, v = Math.max(0, Math.min(100, value));
  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90"><circle cx={size / 2} cy={size / 2} r={r} stroke="var(--line)" strokeWidth={stroke} fill="none" /><circle cx={size / 2} cy={size / 2} r={r} stroke={BAR_COLORS[tone]} strokeWidth={stroke} fill="none" strokeDasharray={c} strokeDashoffset={c - (v / 100) * c} strokeLinecap="round" className="transition-[stroke-dashoffset] duration-700" /></svg>
      <span className="t1 absolute tnum text-[11px] font-bold">{children ?? `${v}%`}</span>
    </div>
  );
}

export const TIP_STYLE = { borderRadius: 6, border: "1px solid var(--line-strong)", background: "var(--surface)", color: "var(--ink)", fontSize: 12, fontFamily: "var(--font-ui)", fontVariantNumeric: "tabular-nums", padding: "6px 10px" };
